import { createTypedEnum } from '@reckon-web/formschema-enums';

import { GetUserQueryItem } from '../data/useGetUser';

const SecurityRoleEnum = createTypedEnum({
  Approver: 'Approver',
  WorkflowAdministrator: 'WorkflowAdministrator',
  Reviewer: 'Reviewer',
  Staff: 'Staff',
} as const);

export const SecurityRole = SecurityRoleEnum.valuesEnum;

export type SecurityRoleType = (typeof SecurityRoleEnum.values)[number];
export type SecurityRoleTypeValue = (typeof SecurityRoleEnum.labels)[number];

const rolesThatCanUpdateJob: Set<SecurityRoleType> = new Set([
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
  SecurityRole.Reviewer,
  SecurityRole.Staff,
]);
const rolesThatCanDeleteJob: Set<SecurityRoleType> = new Set([
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
  SecurityRole.Reviewer,
]);

const rolesThatCanDeleteTask: Set<SecurityRoleType> = new Set([
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
  SecurityRole.Reviewer,
]);

const rolesThatCanApproveSignOff: Set<SecurityRoleType> = new Set([
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
]);
const rolesThatCanDeclineSignOff: Set<SecurityRoleType> = new Set([
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
]);
const rolesThatCanRevokeSignOff: Set<SecurityRoleType> = new Set([
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
]);

const rolesThatCanRequestSignOff: Set<SecurityRoleType> = new Set([
  SecurityRole.Reviewer,
  SecurityRole.Staff,
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
]);
const rolesThatCanWithdrawSignOff: Set<SecurityRoleType> = new Set([
  SecurityRole.Reviewer,
  SecurityRole.Staff,
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
]);
const rolesThatCanEditTemplates: Set<SecurityRoleType> = new Set([
  SecurityRole.Reviewer,
  SecurityRole.Approver,
  SecurityRole.WorkflowAdministrator,
]);

export type SecurityUser = GetUserQueryItem;

// {
//   active?: boolean | null;
//   roleName?: string | null;
//   workflowRole?: SecurityRole | null;
//   administrator?: boolean | null;
//   licensed?: boolean | null;
//   manager?: boolean | null;
//   partner?: boolean | null;
// };

// for all of these set lookups, we don't actually care if the users role is null/empty/etc
// the set lookup works just as well with null/undefined (it's not in the set)
// so we'll shut the compiler up with a !

// edit templates is actually "manage" which covers view the list, edit them, delete them, clone them
export function canEditTemplates(user?: SecurityUser | null) {
  return rolesThatCanEditTemplates.has(user?.workflowRole!);
}

// anyone can create or edit jobs
export function canCreatejob(user?: SecurityUser | null) {
  return true;
}
export function canEditJob(user?: SecurityUser | null) {
  return true;
}

// anyone can be staff on a job
export function canStaffJob(user?: SecurityUser | null) {
  return user?.licensed && user?.active;
}
export function canManagerJob(user?: SecurityUser | null) {
  return user?.manager && user?.licensed && user.active;
}
export function canPartnerJob(user?: SecurityUser | null) {
  return user?.partner && user?.licensed && user?.active;
}
export function canUpdateJob(user?: SecurityUser | null) {
  return rolesThatCanUpdateJob.has(user?.workflowRole!);
}
export function canDeleteJob(user?: SecurityUser | null) {
  return rolesThatCanDeleteJob.has(user?.workflowRole!);
}
export function canReopenJob(user?: SecurityUser | null) {
  return user?.administrator;
}
export function canBeAssignedTask(user?: SecurityUser | null) {
  return user?.licensed && user.active;
}
export function canDeleteTask(user?: SecurityUser | null) {
  return rolesThatCanDeleteTask.has(user?.workflowRole!);
}
export function canEditTask(user?: SecurityUser | null) {
  return true;
}
export function canViewRecurrences(user?: SecurityUser | null) {
  return canStaffJob(user);
}
export function canApproveSignOff(user?: SecurityUser | null) {
  return (
    rolesThatCanApproveSignOff.has(user?.workflowRole!) &&
    user?.licensed &&
    user?.active
  );
}
export function canDeclineSignOff(user?: SecurityUser | null) {
  return (
    rolesThatCanDeclineSignOff.has(user?.workflowRole!) &&
    user?.licensed &&
    user?.active
  );
}
export function canRevokeSignOff(user?: SecurityUser | null) {
  return (
    rolesThatCanRevokeSignOff.has(user?.workflowRole!) &&
    user?.licensed &&
    user?.active
  );
}
export function canViewSignOffs(user?: SecurityUser | null) {
  return (
    canApproveSignOff(user) || canDeclineSignOff(user) || canRevokeSignOff(user)
  );
}
export function canRequestSignOff(user?: SecurityUser | null) {
  return (
    rolesThatCanRequestSignOff.has(user?.workflowRole!) &&
    user?.licensed &&
    user?.active
  );
}
export function canWithdrawSignOff(user?: SecurityUser | null) {
  return (
    rolesThatCanWithdrawSignOff.has(user?.workflowRole!) &&
    user?.licensed &&
    user?.active
  );
}

// we could, in the future, add a canView and canAdmin for users. But, for now, this'll do
export function canAdminUsers(user?: SecurityUser | null) {
  return user?.administrator && user?.active && user?.licensed;
}

// controls whether the user can access TUM.
// Very specifically looking for a very specific role name here, not just the administrator flag on the user
const GLOBAL_ROLE_ROOT = 'root';
export function canTUM(user?: SecurityUser | null) {
  return user?.roleName === GLOBAL_ROLE_ROOT;
}

export function canManageJobStatuses(user?: SecurityUser | null) {
  return user?.administrator && user?.active && user?.licensed;
}
